import React, { useState, useEffect } from 'react';
import { Mail, MapPin, ArrowRight } from 'lucide-react';

// Subtle ambient background - abstract particles, no mechanism details
const AmbientBackground = () => {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    let frame;
    const animate = () => {
      setRotation((prev) => (prev + 0.15) % 360);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  const particles = Array.from({ length: 24 }, (_, i) => ({
    x: Math.sin(i * 0.9 + rotation * 0.01) * 45 + 50,
    y: Math.cos(i * 0.6 + rotation * 0.008) * 40 + 50,
    size: 1.5 + Math.sin(i + rotation * 0.02) * 1,
    opacity: 0.15 + Math.sin(i * 0.4 + rotation * 0.01) * 0.1,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="ambient-bg">
      {/* Radial glows */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 40%, rgba(59, 130, 246, 0.12), transparent 60%)',
        }}
      />
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
        style={{ background: 'rgba(6, 182, 212, 0.08)' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
        style={{ background: 'rgba(59, 130, 246, 0.08)' }}
      />

      {/* Floating particles */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {particles.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={p.size * 0.15} fill="#3b82f6" opacity={p.opacity} />
        ))}
      </svg>

      {/* Noise overlay */}
      <div className="absolute inset-0 noise-overlay" />
    </div>
  );
};

const SinglePage = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="relative min-h-screen bg-[#020617] flex flex-col" data-testid="single-page">
      <AmbientBackground />

      {/* Header - Logo */}
      <header className="relative z-10 pt-8 px-6">
        <div className="max-w-5xl mx-auto flex items-center gap-3" data-testid="logo">
          <div className="relative">
            <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <span className="text-2xl font-bold text-white font-['Outfit']">T</span>
            </div>
            <div className="absolute inset-0 rounded-lg bg-blue-500 blur-lg opacity-30"></div>
          </div>
          <div>
            <span className="text-xl font-semibold text-white font-['Outfit'] tracking-tight">
              Timoleon
            </span>
            <span className="text-xl font-light text-slate-400 font-['Outfit'] tracking-tight ml-1.5">
              Therapeutics
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-3xl mx-auto text-center">
          {/* Confidential badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-10 animate-slide-up">
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
            <span className="text-sm text-slate-300 font-medium tracking-wide">
              Confidential · Stealth Mode
            </span>
          </div>

          {/* Main statement */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-8 animate-slide-up stagger-1">
            <span className="text-gradient">Redefining the Therapeutic</span>
            <br />
            <span className="text-white">Window in Oncology.</span>
          </h1>

          {/* Description */}
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto mb-6 animate-slide-up stagger-2">
            We are a biotech startup building a platform to solve the industry's greatest
            challenge:
          </p>

          <p className="text-xl md:text-2xl font-medium text-white mb-12 animate-slide-up stagger-2">
            on-target, off-tumor toxicity.
          </p>

          {/* Pre-seed raise card */}
          <div
            className="rounded-2xl bg-slate-900/60 border border-blue-500/20 backdrop-blur-sm p-8 md:p-10 max-w-xl mx-auto animate-slide-up stagger-3"
            data-testid="preseed-card"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/25 mb-5">
              <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
                Now Raising · Pre-Seed
              </span>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-3">
              We are raising our Pre-Seed round
            </h2>
            <p className="text-slate-400 mb-8">
              We are speaking with a select group of aligned investors. If you'd like to learn
              more, we'd be glad to connect.
            </p>
            <a
              href="mailto:info@timoleontx.com?subject=Pre-Seed Investment Inquiry"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]"
              data-testid="investor-email-btn"
            >
              <Mail size={18} />
              Contact Us
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 px-6" data-testid="footer">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Email */}
          <a
            href="mailto:info@timoleontx.com"
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="email-link"
          >
            <Mail size={16} className="text-blue-400" />
            <span className="text-sm">info@timoleontx.com</span>
          </a>

          {/* Address */}
          <div className="flex items-start gap-2 text-center md:text-left" data-testid="company-address">
            <MapPin size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-500">
              71-75 Shelton Street, Covent Garden, London, United Kingdom, WC2H 9JQ
            </span>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-6 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-600">
            © {currentYear} Timoleon Therapeutics Limited. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="/privacy-policy" className="text-xs text-slate-600 hover:text-slate-400 transition-colors">
              Privacy Policy
            </a>
            <a href="/terms-of-service" className="text-xs text-slate-600 hover:text-slate-400 transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SinglePage;
